import React from 'react'

import { Box, Stack, Tooltip, Typography, alpha, useTheme } from '@mui/material'
import type { LucideIcon } from 'lucide-react'

import { CONTROL_HEIGHT } from '@repo/ui'

/**
 * ── THE LOW-EMPHASIS VIEW TOGGLE ────────────────────────────────────────────────────
 *
 * A two-or-three-way switch for HOW A SINGLE CARD IS DRAWN — chart type, which points a
 * map plots, which of two renderings of the same data you are looking at. It is card
 * furniture, and it is deliberately quieter than every other toggle in the app.
 *
 * ## Why this is NOT `SegmentedTabs`
 *
 * 🚨 The point of this component, and the thing to read before "consolidating" it back.
 *
 * `SegmentedTabs` is the control for a PAGE-LEVEL choice — one that reshapes the whole
 * screen. The settings pages' tabs change which records you are looking at; the recessed
 * track, the sliding pill and the 48px band are the correct volume for that.
 *
 * A chart's line-vs-bar, a map's all-vs-latest, and which of two dimensions ONE card
 * counts are not that. They are per-card, instantly-reversible, and scoped to the card
 * they sit in. Drawing them with the same control implies the two decisions carry equal
 * weight, and a reader who has learned that the pill means "this changes everything" is
 * misled twice: once by the card control that does not, and once by the page control
 * whose signal is now diluted. The volume of a control is part of what it says.
 *
 * ⚠️ THE AXIS IS CONSEQUENCE, NOT SUBJECT MATTER — and this note used to get that wrong.
 * It read "`SegmentedTabs` is the control for switching WHAT IS COUNTED", citing the
 * analytics page's Scans / Certificates pill as changing "every figure on the screen".
 * That was false when it was written — the four KPI tiles never followed it — and the
 * pill has since been demoted into the two card headers it actually scoped
 * (`AnalyticsDimensionToggle`). It is a `ViewToggle` now, and it does switch what is
 * counted. Reach for the pill by how far a choice reaches, not by what it is about.
 *
 * ## Two label forms, ONE control — and the difference is the CONTENT's, not a style choice
 *
 * `icon` present → an icon button, with the label as its tooltip and its accessible name.
 * `icon` absent → a text button showing the label.
 *
 * Line and bar iconify naturally: `ChartLine` and `ChartColumn` are conventional glyphs a
 * reader decodes without a legend. "All scans" and "Latest location" do NOT — there is no
 * established glyph for either, and inventing a pair would make the control less legible
 * rather than lighter. So the map keeps words. Both end up as the same weight of card
 * furniture; they differ only in the form their content actually supports. ⚠️ Do not
 * "finish the job" by iconifying the text consumers — matching the WEIGHT was the goal,
 * not matching the mechanism.
 *
 * ## The selected state never rests on colour alone
 *
 * Losing the pill loses the strongest available channel, so the state is carried twice in
 * both forms — colour AND weight, expressed in whatever "weight" means for the medium:
 *
 *   text  selected `text.primary` @ 600 · unselected `text.secondary` @ 400
 *   icon  selected `text.primary` @ strokeWidth 2 · unselected `text.secondary` @ 1
 *
 * ## Accessibility is load-bearing here, not a checklist item
 *
 * A real `<button>` per option with `aria-pressed` (the group is a set of toggles, not a
 * radio group — `aria-pressed` is what a screen reader announces as "pressed"), an
 * explicit `aria-label` so the icon form has a name at all, a `role="group"` wrapper
 * carrying what the group as a whole switches, and a visible `:focus-visible` outline in
 * the house treatment (`2px solid brand[600]`, offset 1 — the same ring `ScanTargetButton`
 * uses). Without the track there is no other focus affordance, so the ring is the only
 * thing making the control keyboard-usable.
 *
 * ## Size
 *
 * `CONTROL_HEIGHT` (40) for the hit box, matching `AnalyticsCardShell`'s own grip / expand
 * / remove icon buttons, which sit in the same header action row. Those are 40 for exactly
 * this reason and a 28px neighbour would read as a different class of control. The 40 is
 * a TARGET, not a drawn box: nothing is painted at rest, so the pair is visually two
 * glyphs (or two words) rather than the 124×48 track it replaces.
 *
 * ⚠️ THE HEIGHT IS THE NEIGHBOUR'S, NOT THE CONTROL'S — which is why `height` is a prop.
 *
 * The 40 above is an argument about ONE context: a card header action row whose other
 * controls are 40. It is not a property this control carries everywhere, and there is a
 * second legitimate context that answers to a different neighbour — a MAP-CANVAS CHIP.
 * There, the toggle is not bare: it sits INSIDE a drawn 40px chip (`ScanMapPanel`'s
 * bordered, shadowed pill floating over the map) alongside the certificate map's Journey
 * chip, which is a 40px `Button`. So the thing that must measure 40 is the CHIP, and a
 * 40px control inside a 40px chip has nowhere to put its hover wash — it painted right up
 * to and across the chip's top edge, which is the defect this prop fixes. Inside a drawn
 * container the control is sized to leave the container an inset; the container carries
 * the 40.
 *
 * This is the same reasoning shape as the `SegmentedTabs`-vs-`ViewToggle` note above: pick
 * by what the control ANSWERS TO, not by a number that happened to be right once. Deliberately
 * a plain number rather than a `size="small"` enum — a named shrunken tier invites "use the
 * small one here" as an aesthetic choice, which is exactly what working-conventions §60
 * argues against for `Button`; a height is a statement about a specific neighbour and has to
 * be justified per call site.
 */

export interface ViewToggleOption<T extends string> {
  value: T
  /**
   * Always required. In text form it is what is drawn; in icon form it is the tooltip AND
   * the accessible name, so an icon option without one would be an unlabelled button.
   */
  label: string
  /**
   * Present → this option renders as an icon. A COMPONENT, not a rendered element: the
   * control owns `size` and `strokeWidth`, because `strokeWidth` is the selected-state
   * weight channel and a pre-rendered `<Icon />` could not be varied by state.
   *
   * Typed as `LucideIcon` rather than a structural
   * `ComponentType<{ size?: number; strokeWidth?: number }>`, which does not compile: a
   * lucide glyph is a `ForwardRefExoticComponent`, and that is neither a `FunctionComponent`
   * nor a `ComponentClass`, so it is not assignable to `ComponentType` however loosely its
   * props are described. Naming the real type is honest about the coupling — every icon in
   * this app is a lucide glyph by convention (§21) — and it is what makes `strokeWidth`
   * type-safe rather than a prop the control hopes the icon accepts.
   */
  icon?: LucideIcon
  /**
   * This option cannot be selected right now — a §46 RECORD-STATE block (e.g. not
   * enough data for this view to be useful yet), not a permission. Rendered visible
   * and dimmed rather than hidden, because unlike a permission block the reason here
   * is usually temporary and the option is worth advertising even while it can't be
   * picked (VERA-260 heat-map mode: hidden entirely, a reader would have no way to
   * learn the mode exists at all until the data caught up).
   *
   * MUST be paired with `disabledReason` — §46's own forcing function: if there is no
   * sentence to explain why, this is not a disable case.
   */
  disabled?: boolean
  /** Shown as a tooltip on the disabled option. Required together with `disabled`. */
  disabledReason?: string
}

interface ViewToggleProps<T extends string> {
  options: readonly ViewToggleOption<T>[]
  value: T
  onChange: (next: T) => void
  /** Names what the GROUP switches — "Chart type", "Map points". Required: see the a11y note. */
  ariaLabel: string
  /**
   * Hit-box height, in px. Defaults to `CONTROL_HEIGHT` (40) — every card-header call site
   * gets exactly what it had.
   *
   * Pass a smaller value ONLY when the toggle sits inside a DRAWN container that itself
   * owns the 40 (a map-canvas chip), so the hover wash has an inset to breathe into rather
   * than painting across the container's edge. See the "size" section of the class docblock
   * for the full argument. In icon form this is also the square button's width.
   */
  height?: number
}

const ICON_SIZE = 18

export const ViewToggle = <T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  height = CONTROL_HEIGHT,
}: ViewToggleProps<T>) => {
  const theme = useTheme()

  return (
    // `role="group"`, not `radiogroup`: these are independent toggle buttons carrying
    // `aria-pressed`, and a radiogroup would promise arrow-key roving this does not
    // implement. Tab moves between them, which is the honest behaviour for buttons.
    // `flexWrap` + `maxWidth: 100%` (VRG-643): a set wider than its column — three
    // "Approved (24)"-style labels at a phone width — wraps onto a second line instead of
    // running past the card and making the page scroll sideways. A set that fits renders
    // exactly as before; there is no track or pill here, so a wrapped line reads as more of
    // the same buttons.
    <Stack
      direction="row"
      role="group"
      aria-label={ariaLabel}
      alignItems="center"
      sx={{ flexWrap: 'wrap', maxWidth: '100%' }}
    >
      {options.map(option => {
        const selected = option.value === value
        const Icon = option.icon
        const isDisabled = option.disabled === true

        const button = (
          <Box
            key={option.value}
            component="button"
            type="button"
            aria-pressed={selected}
            aria-label={option.label}
            aria-disabled={isDisabled || undefined}
            onClick={() => {
              if (isDisabled) return
              if (!selected) onChange(option.value)
            }}
            sx={{
              // UA button chrome reset — the same list `StatusPill` documents. Without it
              // the UA supplies its own 13.333px font, centring and margin, so a text
              // option would not match the `body3` beside it.
              font: 'inherit',
              margin: 0,
              border: 'none',
              appearance: 'none',
              backgroundColor: 'transparent',
              cursor: isDisabled ? 'default' : 'pointer',

              height,
              minWidth: Icon ? height : undefined,
              px: Icon ? 0 : 3,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 2,

              color: selected ? theme.palette.text.primary : theme.palette.text.secondary,
              // Dimmed rather than a separate greyscale palette — matches the modest
              // weight this whole control already reads at (no track, no pill).
              opacity: isDisabled ? 0.45 : 1,

              // Hover paints a transient wash so the hit area is discoverable — the same
              // affordance the card shell's own icon buttons carry. It is NOT a resting
              // background: a resting one would be the pill this component exists to drop.
              // Suppressed entirely while disabled — nothing to discover, and a hover wash
              // on a button that won't respond reads as a bug.
              ...(isDisabled
                ? {}
                : {
                    '&:hover': {
                      color: theme.palette.text.primary,
                      backgroundColor: alpha(theme.palette.text.primary, 0.06),
                    },
                  }),
              '&:focus-visible': {
                outline: `2px solid ${theme.palette.brand[600]}`,
                outlineOffset: 1,
              },
            }}
          >
            {Icon ? (
              // The second state channel. See the class docblock — with no pill, colour
              // alone would be the only signal.
              <Icon size={ICON_SIZE} strokeWidth={selected ? 2 : 1} />
            ) : (
              <Typography
                variant="body3"
                component="span"
                fontWeight={selected ? 600 : 400}
                // `color: inherit` so the button's own hover/selected colour reaches the
                // label rather than being overridden by a second, static one.
                sx={{ color: 'inherit', whiteSpace: 'nowrap' }}
              >
                {option.label}
              </Typography>
            )}
          </Box>
        )

        // A tooltip in icon form always (label is otherwise invisible), OR whenever
        // disabled — §46's forcing function: a disabled control MUST carry a tooltip
        // naming why, on a text option exactly as much as an icon one.
        const tooltipTitle = isDisabled && option.disabledReason ? option.disabledReason : option.label
        return Icon || isDisabled ? (
          <Tooltip key={option.value} title={tooltipTitle} placement="top" arrow>
            {button}
          </Tooltip>
        ) : (
          button
        )
      })}
    </Stack>
  )
}
